import { createSlice, current } from "@reduxjs/toolkit";
import urls from "../constants/api-endpoints";
import helpers from "../utils/helpers";
import accessToken from "../utils/access-token";
import axios from 'axios'
import { setSnackBar } from "./ToastSlice";
import { MixPanelConstants } from "../constants/MixPanelEvents";
import { MixpanelTracking } from "../MixPanel";
import { toggleLinearLoader } from "./LoaderSlice";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosPromise } from "../utils/axios";

export const states = Object.freeze({
    IDLE : 1,
    IN_PROGRESS : 2,
    DONE : 3
});

export const responseStates : any = Object.freeze({
    SUCCESS : 1,
    FAILED : 2,
    IDLE : 3
})

const baseURL = process.env.NEXT_PUBLIC_SERVER_BASE_URL;

const coreSlice = createSlice({
    name : 'core',
    initialState : {
        dealList : {
            status : '',
            data : {},
            list : []
        },
        apiList :{
            status : states.IDLE,
            data : {},
            list : []
        },

        countryList : {
            list : [],
            fixedList : [],
            status : ''
        },

        certificateList : {
            list : [],
            status : ''
        },

        unitList : {
            list : [],
            status : ''
        },

        pharmacopoeiaGradesList : {
            list : [],
            status : ''
        },

        enquiryList : {
            list : [],
            data : {},
            status : ''
        },

        licenseList : {
            list : [],
            data : {},
            status : ''
        },

        userList : {
            list : [],
            data : {},
            status : ''
        },

        enquiryState : {
            responseData : null,
            status : states.IDLE,
            showSuccessModal : false,
            enquiryFormModal : false,
            enquiryFormModalData : {}
        },

        businessSegmentList : {
            list : [],
            status : ''
        },

        files : [],

        heroSectionVisible : false,

        isSellerSubdomain : false,
        isAdminSubdomain : false,

        blogList : {
            list : [],
            status : '',
            data : {},
            searchList : []
        },

        orderPayload : {
            status : states.IDLE,
            responseStatus : responseStates.IDLE,
            cart_items : []
        }
    },
    reducers : {
        setAPIList(state, data) {
            state.apiList.data = data.payload;
            state.apiList.list = data.payload.results;
            state.apiList.status = states.IDLE;
        },

        setDealList(state, data) {
            state.dealList.data = data.payload;
            state.dealList.list = data.payload.results;
        },

        setCountryList(state, data) {
            state.countryList.list = data.payload;

            if(!state.countryList.fixedList.length) {
                state.countryList.fixedList = data.payload.map((payload : any) => {
                    return {...payload, label : payload.dial_code, value : payload.dial_code}
                })
            }
        },

        setCertificateList(state, data) {
            state.certificateList.list = data.payload.map((data : any) => {
                return {
                    ...data, 
                    value : data.id,
                    label : data.name
                }
            });
        },

        setUnitList(state, data) {
            state.unitList.list = data.payload.map((data : any) => {
                return {
                    ...data, 
                    value : data.id,
                    label : data.name
                }
            });
        },

        setPharmacopoeiaGrades(state, data) {
            state.pharmacopoeiaGradesList.list = data.payload.map((data : any) => {
                return {
                    ...data, 
                    value : data.id,
                    label : data.name
                }
            });
        },

        setLicenseList(state, data) {
            state.licenseList.list = data.payload.map((data : any) => {
                return {
                    ...data, 
                    value : data.id,
                    label : data.name
                }
            });
        },

        setEnquirySuccessData(state : any, data : any) {
            state.enquiryState.enquiryFormModal = false;
            state.enquiryState.showSuccessModal = true;
            state.enquiryState.status = states.IDLE;
            state.enquiryState.responseData = data.payload;

            for(let data in state.enquiryState.enquiryFormModalData)
                delete state.enquiryState.enquiryFormModalData[data];
        },

        openEnquiryModal(state, data) {
            state.enquiryState.enquiryFormModal = true;
            state.enquiryState.enquiryFormModalData = data.payload;
        },

        closeEnquiryModal(state) {
            state.enquiryState.enquiryFormModal = false;
        },

        closeEnquirySuccessModal(state) {
            state.enquiryState.showSuccessModal = false;
            state.enquiryState.responseData = null;
        },

        setHeroSectionVisible(state, data) {
            state.heroSectionVisible = data.payload;
        },

        setIsSellerSubdomain(state, data) {
            state.isSellerSubdomain = data.payload;
        },

        setIsAdminSubdomain(state, data) {
            state.isAdminSubdomain = data.payload;
        },

        setBusinessSegmentList(state, data) {
            state.businessSegmentList.list = data.payload;
        },

        setUserList(state, data) {
            state.userList.list = data.payload; 
        },

        setEnquiryList(state, data) {
            state.enquiryList.list = data.payload.results;
            delete data.payload['results'];
            state.enquiryList.data = data.payload;
        },

        setFileData(state : any, data : any) {
            if(data.payload.action === 'replace')
                state.files = data.payload.data;
            else
                state.files = [...state.files, ...data.payload.data]
        },

        setBlogs(state : any, data : any) { 
            if(data.payload.search)
                state.blogList.searchList = data.payload.results;
            else {
                if(data.payload.type === 'append')
                    state.blogList.list = [...state.blogList.list, ...data.payload.results];
                else
                    state.blogList.list = data.payload.results;
            }

            delete data.payload['results'];
            state.blogList.data = data.payload;    
        },

        setOrderPayload(state : any, data : any) {
            state.orderPayload = {
                ...state.orderPayload,
                ...data.payload
            }
        },

        resetOrderPayload(state : any) {
            state.orderPayload = {
                responseStatus : responseStates.IDLE,
                status : states.IDLE,
                cart_items : []
            }
        }
    },

    extraReducers : (builder) => {
        // LOADING STATE FOR PRODUCT LIST
        builder.addCase(getAPIList.pending, (state : any, action) => {
            state.apiList.status = states.IN_PROGRESS;
        });

        builder.addCase(getAPIList.fulfilled, (state, action) => { 
            state.apiList.status = states.IDLE;
            state.apiList.list = action.payload.results;
            state.apiList.data = action.payload;
        });

        // LOADING STATE FOR ORDER SUBMISSION
        builder.addCase(submitOrder.pending, (state : any, action : any) => {
            state.orderPayload.status = states.IN_PROGRESS
        })

        builder.addCase(submitOrder.fulfilled, (state, action) => { 
            if(action.payload?.status !== 201) {
                state.orderPayload = {
                    responseStatus : responseStates.FAILED,
                    status : states.IDLE,
                    cart_items : []
                }
            }
            else {
                state.orderPayload = {
                    responseStatus : responseStates.SUCCESS,
                    status : states.IDLE,
                    cart_items : []
                }
            }
        });

        builder.addCase(submitOrder.rejected, (state, action) => { 
            state.orderPayload = {
                responseStatus : responseStates.FAILED,
                status : states.IDLE,
                cart_items : []
            }
        });
    }
});

export default coreSlice.reducer;
export const { 
    setDealList, 
    setAPIList, 
    setCountryList, 
    setCertificateList, 
    closeEnquiryModal,
    closeEnquirySuccessModal,
    setEnquirySuccessData,
    openEnquiryModal,
    setHeroSectionVisible,
    setIsSellerSubdomain,
    setUnitList,
    setPharmacopoeiaGrades,
    setBusinessSegmentList,
    setEnquiryList,
    setLicenseList,
    setFileData,
    setIsAdminSubdomain,
    setBlogs,
    setOrderPayload,
    resetOrderPayload,
    setUserList
} = coreSlice.actions;

export function user() {
    return (dispatch : any, getState : any) => {
        const user = getState().auth.user;
        return user;
    };
}

// THUNKS FOR ASYNC CALL OR API CALLS

// GET DEAL LIST
export const getDealList = (params : object) => {
    return async function getDealListThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.dealList);

        let headers = {}

        if(accessToken.getAccessToken()) {
            headers = {
                'Authorization' : `Bearer ${accessToken.getAccessToken()}`
            }
        }

        const successHandler = (response : any) => {
            if(response.status === 200) {
                const dealList = response.data.results.map((data : any) => {
                    return {...data, shrinkedCertificates : data.product_details.certificate_list.slice(0, 3)}
                });
                response.data.results = dealList;
                dispatch(setDealList(response.data));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            params,
            headers,
            successHandler,
            errorHandler
        )
    }
}

// GET PRODUCT LIST
export const getAPIList : any = createAsyncThunk("core/getAPIList", async(params : object) => {
    let data = {};

    const endPoint = helpers.getAPIEndpoint(urls.productList);

    const successHandler = (response : any) => {
        if(response.status === 200) {
            data = response.data;
        }
    }

    const errorHandler = (error : any) => {
        console.log(error);
    }

    await AxiosPromise.requestGET(
        endPoint,
        params,
        {},
        successHandler,
        errorHandler
    )

    return data;
})


// GET COUNTRY LIST
export const getCountryList = (params : object = {}) => {
    return async function getCountryListThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.countries);

        const successHandler = (response : any) => {
            if(response.status === 200) {
                dispatch(setCountryList(response.data));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            params,
            {},
            successHandler,
            errorHandler
        )
    }
}

// GET CERTIFICATE LIST     
export const getCertificateList = () => {
    return async function getCertificateListThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.certificates);

        const successHandler = (response : any) => {
            if(response.status === 200) {
                dispatch(setCertificateList(response.data));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            {},
            {},
            successHandler,
            errorHandler
        )
    }
}

// GET UNIT LIST
export const getUnitList = () => {
    return async function getUnitListThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.units);

        const successHandler = (response : any) => {
            if(response.status === 200) {
                dispatch(setUnitList(response.data));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            {},
            {},
            successHandler,
            errorHandler
        )
    }
}

// GET pharmacopoeiaGrades LIST
export const getPharmacopoeiaGrades = () => {
    return async function getPharmacopoeiaGradesThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.pharmacopoeiaGrades);

        const successHandler = (response : any) => {
            if(response.status === 200) {
                dispatch(setPharmacopoeiaGrades(response.data));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            {},
            {},
            successHandler,
            errorHandler
        )
    }
}

// GET LICENSE LIST
export const getLicenseList = () => {
    return async function getLicenseListThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.license);

        const successHandler = (response : any) => {
            if(response.status === 200) {
                dispatch(setLicenseList(response.data));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            {},
            {},
            successHandler,
            errorHandler
        )
    }
}

// GET BUSINESS SEGMENTS
export const getBusinessSegments = () => {
    return async function getBusinessSegmentsThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.businessSegments);

        const successHandler = (response : any) => {
            if(response.status === 200) {
                dispatch(setBusinessSegmentList(response.data))
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            {},
            {},
            successHandler,
            errorHandler
        )
    }
}

// GET ENQUIRY LIST
export const getEnquiryList = (params : object = {}) => {
    return async function getEnquiryListThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.enquiryList);
        const token = accessToken.getAccessToken();
        let headers = {}

        if(token) {
            headers = {
                'Authorization' : `Bearer ${token}`
            }
        }

        const successHandler = (response : any) => {
            if(response.status === 200) {
                dispatch(setEnquiryList(response.data));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            params,
            headers,
            successHandler,
            errorHandler
        )
    }
}

// SUBMIT ENQUIRY
export const submitEnquiry = (data : any, query : any = {}) => {
    return async function submitEnquiryThunk(dispatch : any, getState : any) {
        const userDetails = dispatch(user());

        const payload = {...data};
        const endPoint = helpers.getAPIEndpoint(urls.enquiry);

        // Converting the array of objects into array of ids for backend
        if(!('direct_purchase' in query)) {
            payload['expected_certificates'] = payload['expected_certificates'].map((value : any) => value.value);
            payload['pharmacopoeia_grades'] = payload['pharmacopoeia_grades'].map((value : any) => value.value);
            
            delete payload['product_string'];
            delete payload['country_string'];
            delete payload['dial_code'];
        }

        const mixPanelData : any = {
            form : 'Enquiry Form',
            userInput : data
        }
       
        const headers : any = {}

        if(userDetails.isLoggedIn) {
            headers['Authorization'] = `Bearer ${accessToken.getAccessToken()}` 
        }

        const successHandler = (response : any) => {
            const responseData = {
                user : userDetails.name || response.data.created_by_name,
                response : response.data
            }

            dispatch(setEnquirySuccessData(responseData));

            mixPanelData['status'] = MixPanelConstants.SUCCESS

            MixpanelTracking.getInstance().formSubmittedEx(mixPanelData, 'direct_purchase' in query && query.direct_purchase);
        }

        const errorHandler = (error : any) => {
            console.log(error);
            let message = '';
            try {
                message = error.response.data.message[0];
            }
            catch {
                message = error.response.data.detail;
            }
            dispatch(setSnackBar(
                {
                    color : 'error',
                    message : message || 'Something went wrong internally!'
                }
            ));
    
            mixPanelData['status'] = MixPanelConstants.FAILED
            mixPanelData['reason'] = message
            MixpanelTracking.getInstance().formSubmittedEx(mixPanelData);
        }

        await AxiosPromise.requestPOST(
            endPoint,
            payload,
            headers,
            successHandler,
            errorHandler,
            query
        )
    }
}

// UPLOAD FILE
export const uploadFile = (data : any, action = 'append') => {
    return async function uploadFileThunk(dispatch : any, getState : any) {

        dispatch(toggleLinearLoader(true));
        const endPoint = helpers.getAPIEndpoint(urls.fileUpload);

        const successHandler = (response : any) => {
            if(response.status === 201) {
                dispatch(setFileData({
                    data : response.data.data,
                    action : action
                }));
            }
            dispatch(toggleLinearLoader(false));
        }

        const errorHandler = (error : any) => {
            dispatch(toggleLinearLoader(false));
            console.log(error);
            dispatch(setSnackBar({
                color : 'error',
                message : 'Unable to upload file. Please try again!'
            }))
        }

        await AxiosPromise.requestPOST(
            endPoint,
            data,
            {},
            successHandler,
            errorHandler
        )
    }
}

// DELETE FILE
export const deleteFile = (data : any) => {
    return async function deleteFileThunk(dispatch : any, getState : any) {
        dispatch(toggleLinearLoader(true));
        const endPoint = helpers.getAPIEndpoint(urls.deleteFile, data);

        const payload = {
            is_active : false,
            in_use : false
        }

        const successHandler = (response : any) => {
            if(response.status === 200) {
                const files = getState().core.files;
                const filteredData = files.filter((file : any) => {
                    return file.id !== data;
                });

                dispatch(setFileData({
                    data : filteredData,
                    action : 'replace'
                }));
            }
            dispatch(toggleLinearLoader(false));
        }

        const errorHandler = (error : any) => {
            dispatch(toggleLinearLoader(false));
            console.log(error);
        }

        await AxiosPromise.requestPATCH(
            endPoint,
            payload,
            {},
            successHandler,
            errorHandler
        )
    }
}

// GET BLOG LIST
export const getBlogList = (data : any = {}) => {
    return async function getBlogListThunk(dispatch : any, getState : any) {
     
        const endPoint = helpers.getAPIEndpoint(urls.blogList);
        const successHandler = (response : any) => {
            if(response.status === 200) {
                if(data.search)
                    response.data['search'] = true;
                
                if(data.type)
                    response.data['type'] = data.type;
    
                dispatch(setBlogs(response.data));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            data,
            {},
            successHandler,
            errorHandler
        )
    }
}

// Submit Order
export const submitOrder : any = createAsyncThunk("core/submitOrder", async (args : any, {getState, dispatch}) => {

    const state : any = getState();    
    const payload : any = {...state.core.orderPayload};

    const mixPanelData : any = {
        requestPayload : payload
    }

    delete payload['status']

    const endPoint = helpers.getAPIEndpoint(urls.order);

    const headers = {
        Authorization : `Bearer ${accessToken.getAccessToken()}`
    }

    let data : any = {};
    const successHandler = (response : any) => {
        if(response.status === 201) {
            data = response.data;
            data['status'] = response.status;
            mixPanelData['status'] = MixPanelConstants.SUCCESS
            MixpanelTracking.getInstance().orderPlaced(mixPanelData);
        }
    }

    const errorHandler = (error : any) => {
        console.log(error);
        data['status'] = error.response.status;
        let message = '';
        try {
            message = error.response.data.message[0];
        }
        catch {
            message = error.response.data.detail;
        }
        dispatch(setSnackBar(
            {
                color : 'error',
                message : message || 'Something went wrong internally!'
            }
        ));
        mixPanelData['status'] = MixPanelConstants.FAILED;
        mixPanelData['reason'] = error.response.data;
        MixpanelTracking.getInstance().orderPlaced(mixPanelData);
    }

    await AxiosPromise.requestPOST(
        endPoint,
        payload,
        headers,
        successHandler,
        errorHandler
    )

    return data;
})

// GET User LIST
export const getUserList = (data : object) => {
    return async function getUserListThunk(dispatch : any, getState : any) {
        const endPoint = helpers.getAPIEndpoint(urls.userList);

        let headers = {
            'Authorization' : `Bearer ${accessToken.getAccessToken()}`
        }

        const successHandler = (response : any) => {
            if(response.status === 200) {
                dispatch(setUserList(response.data.results));
            }
        }

        const errorHandler = (error : any) => {
            console.log(error);
        }

        await AxiosPromise.requestGET(
            endPoint,
            data,
            headers,
            successHandler,
            errorHandler
        )
    }
}
